import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { LoginPage } from '@/pages/LoginPage';
import { OAuthCallbackPage } from '@/pages/OAuthCallbackPage';
import { BuyWizard } from '@/pages/BuyWizard';
import { DashboardPage } from '@/pages/DashboardPage';
import { MarketplacePage } from '@/pages/MarketplacePage';
import { ProductDetailPage } from '@/pages/ProductDetailPage';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AppLayout } from '@/components/layout/AppLayout';
import { useAuth } from '@/hooks/useAuth';
import { Navigate } from 'react-router-dom';
import { set401Handler } from '@/lib/api';
import { useEffect } from 'react';

// Public route wrapper (redirects to dashboard if already authenticated)
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
};


const NegotiationsPage = () => (
  <div>
    <h1 className="text-h1 font-bold text-text-primary">Negotiations</h1>
    <p className="text-base text-text-secondary mt-4">Manage your negotiations</p>
  </div>
);

const InventoryPage = () => (
  <div>
    <h1 className="text-h1 font-bold text-text-primary">Inventory</h1>
    <p className="text-base text-text-secondary mt-4">Manage your inventory</p>
  </div>
);

const OffersPage = () => (
  <div>
    <h1 className="text-h1 font-bold text-text-primary">Offers</h1>
    <p className="text-base text-text-secondary mt-4">Manage your offers</p>
  </div>
);

const RequestsPage = () => (
  <div>
    <h1 className="text-h1 font-bold text-text-primary">Requests</h1>
    <p className="text-base text-text-secondary mt-4">Manage your requests</p>
  </div>
);

const FinancesPage = () => (
  <div>
    <h1 className="text-h1 font-bold text-text-primary">Finances</h1>
    <p className="text-base text-text-secondary mt-4">View financial overview</p>
  </div>
);

// Component to set up 401 handler (needs to be inside Router to use navigate)
const Api401HandlerSetup = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Set up global 401 handler to logout and redirect on authentication failure
    set401Handler(() => {
      logout();
      navigate('/auth/login', { replace: true });
    });
  }, [logout, navigate]);

  return null;
};

const App = () => {
  return (
    <BrowserRouter>
      <Api401HandlerSetup />
      <Routes>
        {/* Authentication routes */}
        <Route path="/auth/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/auth/oauth/callback" element={<OAuthCallbackPage />} />
        
        {/* Protected routes with AppLayout */}
        <Route path="/" element={<ProtectedRoute><AppLayout><DashboardPage /></AppLayout></ProtectedRoute>} />
        <Route path="/marketplace" element={<ProtectedRoute><AppLayout><MarketplacePage /></AppLayout></ProtectedRoute>} />
        <Route path="/marketplace/products/:id" element={<ProtectedRoute><AppLayout><ProductDetailPage /></AppLayout></ProtectedRoute>} />
        <Route path="/buy/:productId/*" element={<ProtectedRoute><AppLayout><BuyWizard /></AppLayout></ProtectedRoute>} />
        <Route path="/negotiations" element={<ProtectedRoute><AppLayout><NegotiationsPage /></AppLayout></ProtectedRoute>} />
        <Route path="/negotiations/:id" element={<ProtectedRoute><AppLayout><div><h1>Negotiation Detail</h1></div></AppLayout></ProtectedRoute>} />
        <Route path="/negotiations/:id/counter" element={<ProtectedRoute><AppLayout><div><h1>Counteroffer Wizard</h1></div></AppLayout></ProtectedRoute>} />
        <Route path="/inventory" element={<ProtectedRoute><AppLayout><InventoryPage /></AppLayout></ProtectedRoute>} />
        <Route path="/inventory/create/*" element={<ProtectedRoute><AppLayout><div><h1>Create Inventory Wizard</h1></div></AppLayout></ProtectedRoute>} />
        <Route path="/offers" element={<ProtectedRoute><AppLayout><OffersPage /></AppLayout></ProtectedRoute>} />
        <Route path="/offers/create/*" element={<ProtectedRoute><AppLayout><div><h1>Create Offer Wizard</h1></div></AppLayout></ProtectedRoute>} />
        <Route path="/requests" element={<ProtectedRoute><AppLayout><RequestsPage /></AppLayout></ProtectedRoute>} />
        <Route path="/requests/create/*" element={<ProtectedRoute><AppLayout><div><h1>Create Request Wizard</h1></div></AppLayout></ProtectedRoute>} />
        <Route path="/finances" element={<ProtectedRoute><AppLayout><FinancesPage /></AppLayout></ProtectedRoute>} />
        <Route path="/finances/receipts" element={<ProtectedRoute><AppLayout><div><h1>Receipts List</h1></div></AppLayout></ProtectedRoute>} />
        <Route path="/receipts/:id" element={<ProtectedRoute><AppLayout><div><h1>Receipt Detail</h1></div></AppLayout></ProtectedRoute>} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;

